import { useCallback, useMemo, useState } from "react";

import { STORAGE_KEYS } from "@utils/storageKeys";

type Props<T extends string> = {
  tableId: string;
  defaultColumnSettings?: Record<T, boolean>;
  defaultColumnOrder?: T[];
};

const parseColumnSettingsFromStorage = (
  tableId: string,
  columnSettings?: string | null,
) => {
  try {
    if (!columnSettings) {
      return;
    }

    return JSON.parse(columnSettings);
  } catch {
    localStorage.removeItem(`${STORAGE_KEYS.TABLE_COLUMN_SETTINGS}_${tableId}`);
    return;
  }
};

const parseColumnOrderFromStorage = (
  tableId: string,
  columnOrder?: string | null,
) => {
  try {
    if (!columnOrder) {
      return;
    }

    return JSON.parse(columnOrder);
  } catch {
    localStorage.removeItem(`${STORAGE_KEYS.TABLE_COLUMN_ORDER}_${tableId}`);
    return;
  }
};

/** Provides the user saved column settings for tables */
export const useTableColumnSettings = <T extends string>({
  tableId,
  defaultColumnSettings,
  defaultColumnOrder,
}: Props<T>) => {
  const [columnSettings, setColumnSettings] = useState<Record<T, boolean>>(
    parseColumnSettingsFromStorage(
      tableId,
      localStorage.getItem(`${STORAGE_KEYS.TABLE_COLUMN_SETTINGS}_${tableId}`),
    ) ?? defaultColumnSettings,
  );

  const [columnOrder, setColumnOrder] = useState<T[]>(
    parseColumnOrderFromStorage(
      tableId,
      localStorage.getItem(`${STORAGE_KEYS.TABLE_COLUMN_ORDER}_${tableId}`),
    ) ??
      defaultColumnOrder ??
      [],
  );

  const updateColumnSettings = useCallback(
    (columnSettings: Record<string, boolean>) => {
      setColumnSettings(columnSettings);
      localStorage.setItem(
        `${STORAGE_KEYS.TABLE_COLUMN_SETTINGS}_${tableId}`,
        JSON.stringify(columnSettings),
      );
    },
    [tableId],
  );

  const updateColumnOrder = useCallback(
    (columnOrder: T[]) => {
      setColumnOrder(columnOrder);
      localStorage.setItem(
        `${STORAGE_KEYS.TABLE_COLUMN_ORDER}_${tableId}`,
        JSON.stringify(columnOrder),
      );
    },
    [tableId],
  );

  const getColumnSettings = useCallback(() => {
    return parseColumnSettingsFromStorage(
      tableId,
      localStorage.getItem(`${STORAGE_KEYS.TABLE_COLUMN_SETTINGS}_${tableId}`),
    );
  }, [tableId]);

  const getColumnOrder = useCallback(() => {
    return parseColumnOrderFromStorage(
      tableId,
      localStorage.getItem(`${STORAGE_KEYS.TABLE_COLUMN_ORDER}_${tableId}`),
    );
  }, [tableId]);

  return useMemo(
    () => ({
      columnSettings,
      updateColumnSettings,
      getColumnSettings,
      columnOrder,
      updateColumnOrder,
      getColumnOrder,
    }),
    [
      columnSettings,
      updateColumnSettings,
      getColumnSettings,
      columnOrder,
      updateColumnOrder,
      getColumnOrder,
    ],
  );
};
